import useSuccessToast from "@configs/react-toastify/snackbar";
import { showErrorToast } from "@configs/react-toastify/snackbar";
import { TOAST } from "@constants/index";
import { useTheme } from "@hooks/useTheme";
import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router";
import { OutlineButton, PrimaryButton } from "@components/Buttons";
import { CustomInput, CustomTextarea, ToggleSwitch } from "@components/Forms";
import { LicensePolicySteps } from "@components/LicensePolicy";
import queryKeys from "@configs/react-query/query-key";
import { useMutationData } from "@hooks/useMutationData";
import { useFetchData } from "@hooks/useFetchData";
import { LicensePolicyExceptionRuleTable } from "@components/Tables";
import Breadcrumb from "@components/UI/Breadcrumb";
import { getPolicyStepColors } from "@constants/licensePolicy";
import { DEFAULT_LICENSE_POLICY, CHARACTER_LENGTH } from "@constants/index";
import { validateDuplicateRules } from "@utils/licensePolicy";
import { handleLicensePolicyMutationError } from "@utils/error";

const EditLicensePolicy = () => {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const navigate = useNavigate();
  const { licensePolicySeq } = useParams();

  const breadcrumbItems = [
    { label: t("license-policy.license-policy"), href: "/license-policy" },
    { label: t("license-policy.license-policy-details") },
  ];

  // Form state
  const [policyData, setPolicyData] = useState({
    policyName: "",
    remark: "",
    globalPolicy: false,
  });

  // Validation state
  const [errors, setErrors] = useState({});
  const [colorErrors, setColorErrors] = useState({});
  const [stepNumberErrors, setStepNumberErrors] = useState({});
  const [policySteps, setPolicySteps] = useState({});
  const [licensePolicyRules, setLicensePolicyRules] = useState([]);

  // Track original data for change detection
  const [originalSteps, setOriginalSteps] = useState({});
  const [originalRules, setOriginalRules] = useState([]);

  // Track which step is currently expanded
  const [expandedStep, setExpandedStep] = useState(null);

  // Track if we've already initialized the first step expansion
  const hasInitializedExpansion = useRef(false);

  // Fetch existing policy
  const { data: fetched, isLoading } = useFetchData(
    [queryKeys.getLicensePolicyByLicenseId, licensePolicySeq],
    `${queryKeys.getLicensePolicyByLicenseId}/${licensePolicySeq}`,
    { method: "GET" },
    {},
    {},
  );

  const policy = useMemo(() => fetched?.object || fetched, [fetched]);

  // Hydrate form when data arrives
  useEffect(() => {
    if (!policy) return;

    setPolicyData({
      policyName: policy.policyName || "",
      remark: policy.remark || "",
      globalPolicy: policy.globalFlag === "Y",
      companyCd: policy.companyCd,
    });

    // Build steps state keyed by index (0, 1, 2...) instead of step number
    const steps = (policy.licensePolicySteps || [])
      .sort((a, b) => Number(a.policyStep) - Number(b.policyStep))
      .reduce((acc, step, index) => {
        acc[index] = {
          stepNumber: step.policyStep, // Store stepNumber as a property
          licensePolicyStepSeq: step.licensePolicyStepSeq,
          licensePolicySeq: licensePolicySeq,
          color: step.policyStepColor || "black",
          approval: step.licenseUseApprovalCd || "APPROVAL",
          remark: step.remark || "",
          db: true,
          licensePolicyStepItems: (step.licensePolicyStepItems || []).map((item) => ({
            licensePolicyStepItemSeq: item.licensePolicyStepItemSeq,
            licensePolicySeq: licensePolicySeq,
            code: item.licensePolicyCd,
            name: item.licensePolicyCdName,
            db: true,
          })),
        };
        return acc;
      }, {});

    // Set steps without adding default step
    setPolicySteps(steps);

    // Set expanded step to first available index or null if no steps
    const firstIndex = Object.keys(steps).map(Number).sort((a, b) => a - b)[0];
    setExpandedStep(firstIndex || null);

    setLicensePolicyRules(policy.licensePolicyRules || []);

    // Store original data for change detection
    setOriginalSteps(steps);
    setOriginalRules(policy.licensePolicyRules || []);
  }, [policy]);

  // Ensure first step is expanded only once during initialization
  useEffect(() => {
    if (!hasInitializedExpansion.current && Object.keys(policySteps).length > 0 && expandedStep === null) {
      const firstIndex = Object.keys(policySteps).map(Number).sort((a, b) => a - b)[0];
      setExpandedStep(firstIndex);
      hasInitializedExpansion.current = true; // Mark as initialized
    }
  }, [policySteps, expandedStep]);

  useEffect(() => {
    validateStepColors();
    validateStepNumbers();
  }, [policySteps]);

  const validateStepNumbers = useCallback(() => {
    const stepCount = {};
    const newStepErrors = {};

    Object.entries(policySteps).forEach(([index, step]) => {
      const stepNum = step.stepNumber;
      if (!stepCount[stepNum]) {
        stepCount[stepNum] = [];
      }
      stepCount[stepNum].push(index);
    });

    Object.entries(stepCount).forEach(([stepNum, indices]) => {
      if (indices.length > 1) {
        indices.forEach(idx => {
          newStepErrors[idx] = "license-policy.error-duplicate-step-number";
        });
      }
    });

    setStepNumberErrors(newStepErrors);
    return Object.keys(newStepErrors).length === 0;
  }, [policySteps]);

  const validateStepColors = useCallback(() => {
    const colorCount = {};
    const newColorErrors = {};

    Object.entries(policySteps).forEach(([index, step]) => {
      const color = step.color;
      if (!color) return;

      if (!colorCount[color]) {
        colorCount[color] = [];
      }
      colorCount[color].push(index);
    });

    Object.entries(colorCount).forEach(([color, indices]) => {
      if (indices.length > 1) {
        indices.forEach(idx => {
          newColorErrors[idx] = "license-policy.error-duplicate-step-color";
        });
      }
    });

    setColorErrors(newColorErrors);
    return Object.keys(newColorErrors).length === 0;
  }, [policySteps]);

  const handleStepChange = (index, stepData) => {
    setPolicySteps((prev) => {
      const currentStep = prev[index];
      const isExistingStep = currentStep?.db === true;

      // For existing steps, we need to handle new items properly
      let updatedStepData = {
        ...stepData,
        db: currentStep?.db || false, // Preserve the db flag
      };

      // If this is an existing step, ensure new items have db: false
      if (isExistingStep && stepData.licensePolicyStepItems) {
        const originalItems = originalSteps[index]?.licensePolicyStepItems || [];

        updatedStepData.licensePolicyStepItems = stepData.licensePolicyStepItems.map(item => {
          const originalItem = originalItems.find(orig => orig.code === item.code);

          return {
            ...item,
            db: originalItem ? true : false, // true for existing, false for new
            licensePolicySeq: licensePolicySeq, // Always include license policy sequence ID
            // Preserve sequence ID for existing items
            ...(originalItem?.licensePolicyStepItemSeq && { licensePolicyStepItemSeq: originalItem.licensePolicyStepItemSeq }),
          };
        });
      }

      return {
        ...prev,
        [index]: updatedStepData,
      };
    });

    // Update licensePolicyRules to reflect the new step color and approval
    setLicensePolicyRules((prevRules) => {
      return prevRules.map((rule) => {
        if (Number(rule.policyStep) === stepData.stepNumber) {
          return {
            ...rule,
            policyStepColor: stepData.color, // Update with new color
            licenseUseApprovalCd: stepData.approval, // Update with new approval
          };
        }
        return rule;
      });
    });
  };

  const addNewStep = () => {
    // Check if maximum steps limit reached
    const stepsCount = Object.keys(policySteps).length;
    // Don't add more than 5 steps
    if (stepsCount >= 5) return;

    // Find the next available color
    const usedColors = Object.values(policySteps).map(step => step.color);
    const allColors = getPolicyStepColors(t).map(color => color.value);
    const availableColor = allColors.find(color => !usedColors.includes(color)) || allColors[0];

    // Get the next available index for the object key (should always be sequential now)
    const nextIndex = stepsCount > 0 ? Math.max(...Object.keys(policySteps).map(Number)) + 1 : 0;

    // Get the next step number (1, 2, 3, 4, 5)
    const existingStepNumbers = Object.values(policySteps).map(step => step.stepNumber);
    let nextStepNumber = 1;

    // Find the next available step number
    while (existingStepNumbers.includes(nextStepNumber)) {
      nextStepNumber++;
    }

    const newStep = {
      stepNumber: nextStepNumber, // Store stepNumber as a property
      color: availableColor,
      approval: "DISAPPROVAL",
      remark: "",
      licensePolicyStepItems: [],
      db: false, // Mark as new step (not in database)
      licensePolicySeq: licensePolicySeq // Store the license policy sequence ID
    };

    setPolicySteps((prev) => ({
      ...prev,
      [nextIndex]: newStep,
    }));
    // Auto-expand the new step by index, not step number
    setExpandedStep(nextIndex);
  };

  const deleteStep = (index) => {
    const deletedStepNumber = policySteps[index]?.stepNumber; // Store before deletion

    // Calculate new steps state after deletion
    const newSteps = { ...policySteps };
    delete newSteps[index];

    // Reindex the remaining steps to have sequential keys (0, 1, 2...)
    const reindexedSteps = {};
    Object.values(newSteps).forEach((step, newIndex) => {
      reindexedSteps[newIndex] = step;
    });

    // Get first available step from NEW state (after deletion)
    const firstAvailableStep = Object.values(reindexedSteps).map(step => step.stepNumber).sort((a, b) => a - b)[0] || 1;
    const firstStepData = Object.values(reindexedSteps).find(step => step.stepNumber === firstAvailableStep);

    // Update state with new steps
    setPolicySteps(reindexedSteps);

    // Update originalSteps to track what was deleted
    setOriginalSteps(prev => {
      const updated = { ...prev };
      // Mark the deleted step as deleted in originalSteps
      if (updated[index]) {
        updated[index] = {
          ...updated[index],
          _deleted: true, // Mark as deleted for tracking
          _deletedAt: Date.now(), // Track when it was deleted
        };
      }
      return updated;
    });

    // Update rules that reference the deleted step or need renumbering
    const updatedRules = licensePolicyRules.map(rule => {
      const ruleStep = Number(rule.policyStep);

      if (ruleStep === deletedStepNumber) {
        // Rule was assigned to the deleted step, reassign to first available step
        return {
          ...rule,
          policyStep: firstAvailableStep,
          policyStepColor: firstStepData?.color || 'black',
          licenseUseApprovalCd: firstStepData?.approval || 'APPROVAL',
        };
      } else if (ruleStep > deletedStepNumber) {
        // Rule was assigned to a step that still exists but may have been renumbered
        // Find the step by matching the original step number in NEW state
        const matchingStep = Object.values(reindexedSteps).find(stepData => stepData.stepNumber === ruleStep);

        if (matchingStep) {
          // Step still exists, just update color and approval to match current step data
          return {
            ...rule,
            policyStep: matchingStep.stepNumber,
            policyStepColor: matchingStep.color,
            licenseUseApprovalCd: matchingStep.approval,
          };
        } else {
          // Step doesn't exist anymore (edge case), reassign to first available step
          return {
            ...rule,
            policyStep: firstAvailableStep,
            policyStepColor: firstStepData?.color || 'black',
            licenseUseApprovalCd: firstStepData?.approval || 'APPROVAL',
          };
        }
      }

      return rule;
    });

    setLicensePolicyRules(updatedRules);

    // Update expanded step if needed
    if (expandedStep === index) {
      // If the deleted step was expanded, set to null
      setExpandedStep(null);
    }
  };

  // Handle step expansion - only allow one step to be expanded at a time
  const handleStepExpand = useCallback((index) => {
    // Only allow toggling if the step is actually different or if we're expanding a new step
    if (expandedStep !== index) {
      setExpandedStep(index);
    } else {
      setExpandedStep(null)
    }
    // If expandedStep === index, don't change it (prevent accidental toggling during re-renders)
  }, [expandedStep]);


  const validateForm = () => {
    const newErrors = {};

    if (!policyData.policyName || policyData.policyName.trim() === '') {
      newErrors.policyName = "license-policy.policy-name-required";
    }

    setErrors(newErrors);

    // Validate duplicate rules
    const isRulesValid = validateDuplicateRules(licensePolicyRules, t);

    return (
      Object.keys(newErrors).length === 0 &&
      Object.keys(colorErrors).length === 0 &&
      Object.keys(stepNumberErrors).length === 0 &&
      isRulesValid
    );
  };

  const updateLicensePolicy = useMutationData(
    queryKeys.createLicensePolicy,
    { method: "PUT" },
    {
      onSuccess: () => {
        useSuccessToast(TOAST.success, t("license-policy.license-policy-updated-success"));
        navigate(`/license-policy/${licensePolicySeq}`);
      },
      onError: (error) => {
        console.error("Error updating license policy:", error);
        handleLicensePolicyMutationError(
          error,
          t,
          showErrorToast,
          t("license-policy.license-policy-updated-error"),
        );
      },
    },
  );

  const handleSave = () => {
    if (!validateForm()) {
      return;
    }

    // Get available step numbers for rule reassignment
    const availableStepNumbers = Object.values(policySteps).map(step => step.stepNumber).sort((a, b) => a - b);
    const firstAvailableStep = availableStepNumbers[0] || 1;

    // Update rules that reference deleted steps or need renumbering
    const updatedLicensePolicyRules = licensePolicyRules.map(rule => {
      const ruleStep = Number(rule.policyStep);

      if (!availableStepNumbers.includes(ruleStep)) {
        // This rule references a deleted step, reassign to first available step
        const firstStepData = Object.values(policySteps).find(step => step.stepNumber === firstAvailableStep);
        return {
          ...rule,
          policyStep: firstAvailableStep,
          policyStepColor: firstStepData?.color || 'black',
          licenseUseApprovalCd: firstStepData?.approval || 'APPROVAL',
        };
      } else {
        // Rule references an existing step, but step numbers might have changed
        // Find the current step number by matching the step data
        const currentStepEntry = Object.entries(policySteps).find(([stepNum, stepData]) => {
          // Try to match by licensePolicyStepSeq first (most reliable)
          if (stepData.licensePolicyStepSeq && rule.licensePolicyStepSeq) {
            return stepData.licensePolicyStepSeq === rule.licensePolicyStepSeq;
          }
          // Fallback: match by color and approval (less reliable but works for new steps)
          return stepData.color === rule.policyStepColor &&
            stepData.approval === rule.licenseUseApprovalCd;
        });

        if (currentStepEntry) {
          const currentStepNumber = currentStepEntry[1].stepNumber; // Use stepNumber property, not index
          const currentStepData = currentStepEntry[1];
          return {
            ...rule,
            policyStep: currentStepNumber,
            policyStepColor: currentStepData.color || rule.policyStepColor,
            licenseUseApprovalCd: currentStepData.approval || rule.licenseUseApprovalCd,
          };
        }
      }

      return rule;
    });

    // Map steps to API structure with proper db/delete flags
    const licensePolicySteps = Object.entries(policySteps)
      .map(([stepIndex, step]) => {
        // Use the stepNumber property from the step data
        const stepNumber = step.stepNumber;

        // Use the db flag directly from the step
        const stepDb = step.db; // true for existing, false for new
        const stepDelete = false; // Never delete in edit mode, just mark for update

        return {
          db: stepDb,
          delete: stepDelete,
          policyStep: stepNumber,
          policyStepColor: step.color,
          licenseUseApprovalCd: step.approval,
          remark: step.remark?.trim(),
          ...(step.licensePolicyStepSeq && { licensePolicyStepSeq: step.licensePolicyStepSeq }),
          licensePolicySeq: Number(licensePolicySeq),
          ...(step.createrId && { createrId: step.createrId }),
          ...(step.createdAt && { createdAt: step.createdAt }),
          licensePolicyStepItems: (step.licensePolicyStepItems || []).map((item) => {
            return {
              db: item.db, // Use the db flag directly from the item
              delete: false, // Never delete items in edit mode
              licensePolicyCd: item.code,
              licensePolicyStepItemSeq: item.licensePolicyStepItemSeq, // Always include the sequence ID
              licensePolicyStepSeq: step.licensePolicyStepSeq, // Include the parent step sequence ID
              licensePolicySeq: Number(licensePolicySeq),
              ...(item.createrId && { createrId: item.createrId }),
              ...(item.createdAt && { createdAt: item.createdAt }),
            };
          }),
        };
      });

    // Add deleted steps with delete: true
    // We need to identify which steps were actually deleted, not just renumbered
    const currentStepNumbers = new Set(Object.keys(policySteps).map(Number));

    // Create a map of current steps by their licensePolicyStepSeq for accurate matching
    const currentStepsBySeq = new Map();
    Object.entries(policySteps).forEach(([stepNum, stepData]) => {
      if (stepData.licensePolicyStepSeq) {
        currentStepsBySeq.set(stepData.licensePolicyStepSeq, stepData);
      }
    });

    const deletedSteps = Object.entries(originalSteps)
      .filter(([stepNum, originalStep]) => {
        // A step is truly deleted if:
        // 1. It was explicitly marked as deleted during deletion, OR
        // 2. Its step number is not in current steps AND its licensePolicyStepSeq is not found in any current step
        const isDeleted = originalStep._deleted === true ||
          (!currentStepNumbers.has(Number(stepNum)) &&
            !currentStepsBySeq.has(originalStep.licensePolicyStepSeq));
        return isDeleted;
      })
      .map(([stepNum, originalStep]) => ({
        db: true,
        delete: true,
        policyStep: Number(stepNum),
        policyStepColor: originalStep.color,
        licenseUseApprovalCd: originalStep.approval,
        remark: originalStep.remark?.trim(),
        licensePolicyStepSeq: originalStep.licensePolicyStepSeq,
        licensePolicySeq: Number(licensePolicySeq),
        ...(originalStep.createrId && { createrId: originalStep.createrId }),
        ...(originalStep.createdAt && { createdAt: originalStep.createdAt }),
        licensePolicyStepItems: (originalStep.licensePolicyStepItems || []).map((item) => ({
          db: true,
          delete: true,
          licensePolicyCd: item.code,
          licensePolicyStepItemSeq: item.licensePolicyStepItemSeq,
          licensePolicyStepSeq: originalStep.licensePolicyStepSeq, // Include the parent step sequence ID
          licensePolicySeq: Number(licensePolicySeq),
          ...(item.createrId && { createrId: item.createrId }),
          ...(item.createdAt && { createdAt: item.createdAt }),
        })),
      }));

    // Identify deleted step items (items that were removed from steps but the step still exists)
    const deletedStepItems = [];

    Object.entries(originalSteps).forEach(([stepNum, originalStep]) => {
      // Only process steps that still exist (not deleted steps)
      if (currentStepNumbers.has(Number(stepNum))) {
        const currentStep = policySteps[stepNum];
        const currentItemCodes = new Set((currentStep.licensePolicyStepItems || []).map(item => item.code));

        // Find items that were in the original step but are not in the current step
        const removedItems = (originalStep.licensePolicyStepItems || []).filter(item =>
          !currentItemCodes.has(item.code)
        );

        // Add these removed items to the deleted items list
        deletedStepItems.push(...removedItems.map(item => ({
          db: true,
          delete: true,
          licensePolicyCd: item.code,
          licensePolicyStepItemSeq: item.licensePolicyStepItemSeq,
          licensePolicyStepSeq: originalStep.licensePolicyStepSeq,
          licensePolicySeq: Number(licensePolicySeq),
          ...(item.createrId && { createrId: item.createrId }),
          ...(item.createdAt && { createdAt: item.createdAt }),
        })));
      }
    });

    // Combine current steps with deleted steps and deleted step items
    const allSteps = [...licensePolicySteps, ...deletedSteps];

    // Add deleted step items to the appropriate steps
    deletedStepItems.forEach(deletedItem => {
      // Find the step that should contain this deleted item
      const targetStep = allSteps.find(step =>
        step.licensePolicyStepSeq === deletedItem.licensePolicyStepSeq
      );

      if (targetStep) {
        // Add the deleted item to the step's licensePolicyStepItems array
        if (!targetStep.licensePolicyStepItems) {
          targetStep.licensePolicyStepItems = [];
        }
        targetStep.licensePolicyStepItems.push(deletedItem);
      }
    });

    // Handle license policy rules with proper flags
    let updatedRules, deletedRules, allRules;

    if (Object.keys(policySteps).length === 0) {
      // If no steps exist, all rules should be marked for deletion
      updatedRules = [];
      deletedRules = originalRules.map(rule => ({
        ...rule,
        db: true,
        delete: true,
        licensePolicySeq: Number(licensePolicySeq),
        ...(rule.createrId && { createrId: rule.createrId }),
        ...(rule.createdAt && { createdAt: rule.createdAt }),
      }));
      allRules = deletedRules;
    } else {
      // Normal rule handling when steps exist
      const currentRuleIds = new Set(updatedLicensePolicyRules.map(rule => rule.licensePolicyRuleSeq));
      const originalRuleIds = new Set(originalRules.map(rule => rule.licensePolicyRuleSeq));

      updatedRules = updatedLicensePolicyRules.map(rule => ({
        ...rule,
        db: originalRuleIds.has(rule.licensePolicyRuleSeq) ? true : false, // true for existing, false for new
        delete: false, // Never delete rules in edit mode
        licensePolicySeq: Number(licensePolicySeq),
        ...(rule.createrId && { createrId: rule.createrId }),
        ...(rule.createdAt && { createdAt: rule.createdAt }),
      }));

      // Add deleted rules with delete: true
      deletedRules = originalRules
        .filter(rule => !currentRuleIds.has(rule.licensePolicyRuleSeq))
        .map(rule => ({
          ...rule,
          db: true,
          delete: true,
          licensePolicySeq: Number(licensePolicySeq),
          ...(rule.createrId && { createrId: rule.createrId }),
          ...(rule.createdAt && { createdAt: rule.createdAt }),
        }));

      allRules = [...updatedRules, ...deletedRules];
    }

    const bodyRequest = {
      db: true,
      delete: false,
      companyCd: policyData.companyCd,
      licensePolicySeq: Number(licensePolicySeq),
      policyName: policyData.policyName?.trim(),
      remark: policyData.remark?.trim(),
      globalFlag: policyData.globalPolicy ? "Y" : "N",
      createrId: policy.createrId,
      createdAt: policy.createdAt,
      licensePolicySteps: allSteps,
      licensePolicyRules: allRules,
    };

    updateLicensePolicy.mutate(bodyRequest);
  };

  const handleCancel = () => {
    navigate(`/license-policy/${licensePolicySeq}`);
  };

  return (
    <div className={`flex h-full w-full flex-col ${colors.bgSecondary}`} data-testid="edit-license-policy-page">
      {/* Breadcrumb */}
      <div className="flex items-center text-sm">
        <Breadcrumb items={breadcrumbItems} />
      </div>

      {/* Header */}
      <div className="flex items-center py-4 justify-end">
        <div className="flex items-center gap-3">
          <OutlineButton onClick={handleCancel}>
            {t("common.cancel-button")}
          </OutlineButton>
          <PrimaryButton data-testid="edit-license-policy-save-button" disabled={policyData.policyName?.trim() === ""} onClick={handleSave}>
            {t("common.save-button")}
          </PrimaryButton>
        </div>
      </div>

      {/* Policy Information - Standalone Section */}
      <div className={`${colors.bgPrimary} rounded-lg p-6 border ${colors.borderPrimary}`} data-testid="edit-license-policy-form">
        <h2 className={`text-md font-medium ${colors.textPrimary} mb-4`}>{t("license-policy.policy-information")}</h2>
        <div className="grid grid-cols-12 gap-6">
          {/* Policy Name */}
          <div className="col-span-3">
            <label className={`block text-sm ${colors.textSubTitle} font-medium mb-2`}>
              {t("license-policy.policy-name")} <span className={colors.textRequired}>*</span>
            </label>
            <CustomInput
              value={policyData.policyName}
              onChange={(e) => {
                setPolicyData((prev) => ({ ...prev, policyName: e.target.value }));
                // Clear error when user starts typing
                if (errors.policyName) {
                  setErrors(prev => ({ ...prev, policyName: '' }));
                }
              }}
              placeholder={t("license-policy.enter-policy-name")}
              size="md"
              disabled={policy?.policyName === DEFAULT_LICENSE_POLICY.POLICY_NAME}
              maxLength={100}
            />
            {errors.policyName && (
              <p className={`${colors.textDanger} text-sm mt-1`}>{t(errors.policyName)}</p>
            )}
          </div>

          {/* Remark */}
          <div className="col-span-7">
            <label className={`block text-sm ${colors.textSubTitle} font-medium mb-2`}>
              {t("license-policy.remark")}
            </label>
            <CustomTextarea
              value={policyData.remark}
              onChange={(e) => setPolicyData((prev) => ({ ...prev, remark: e.target.value }))}
              placeholder={t("license-policy.write-remark")}
              rows={4}
              size="md"
              maxLength={CHARACTER_LENGTH.COMMENT}
            />
            <p className={`text-xs ${colors.textCharacterCount} mt-1`} data-testid="edit-license-policy-remark-char-count">
              {(policyData.remark || "").length}/{CHARACTER_LENGTH.COMMENT}
            </p>
          </div>

          {/* Global Policy */}
          <div className="col-span-2 flex">
            <div className="flex flex-col">
              <label className={`block text-sm ${colors.textSubTitle} font-medium mb-2`}>
                {t("license-policy.default-policy")}
              </label>
              <div className="mt-2">
                <ToggleSwitch
                  checked={policyData.globalPolicy}
                  onChange={() => setPolicyData((prev) => ({ ...prev, globalPolicy: !prev.globalPolicy }))}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* License Policy Item List Container */}
      <LicensePolicySteps
        policySteps={policySteps}
        expandedStep={expandedStep}
        onStepChange={handleStepChange}
        onDeleteStep={deleteStep}
        onStepExpand={handleStepExpand}
        addNewStep={addNewStep}
        colors={colors}
        colorErrors={colorErrors}
        stepNumberErrors={stepNumberErrors}
      />

      {/* License Policy Exception Rule List */}
      <div className={`mt-6 ${colors.bgPrimary} rounded-lg p-6 border ${colors.borderPrimary}`}>
        <h2 className={`text-md font-medium ${colors.textPrimary} mb-4`}>{t("license-policy.license-policy-rules-exception-list")}</h2>
        <LicensePolicyExceptionRuleTable
          data={licensePolicyRules}
          className="w-full"
          onDataChange={(rows) => {
            // Map new rows to include proper flags
            const updatedRows = rows.map((r) => {
              const isExistingRule = originalRules.find(orig => orig.licensePolicyRuleSeq === r.licensePolicyRuleSeq);
              return {
                ...r,
                db: isExistingRule ? true : false, // true for existing, false for new
                delete: false, // Never delete in edit mode
              };
            });
            setLicensePolicyRules(updatedRows);
          }}
          maxRowsPerPage={10}
          isEditMode={true}
          policySteps={policySteps}
        />
      </div>
    </div>
  );
};

export default EditLicensePolicy; 